import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { formatApiError } from "@/lib/utils";
import { useBranding } from "@/lib/branding";
import { KEYFRAMES } from "@/lib/brand";
import {
  Sparkles, Plus, Trash2, Loader2, Mail, Phone, MapPin,
  Calendar, ArrowRight, MessageCircle, ChevronDown, Check,
  Camera, Film, Aperture, Heart, Users, Video, BookOpen, Sunrise,
  Instagram, Star, ArrowDown,
} from "lucide-react";

/**
 * iter132 — Public Inquiry Page (luxury editorial + brand-parity redesign).
 *
 * Zero backend / API / payload changes. Only the visual layer evolves so
 * the inquiry funnel and /proposal/{token} landing feel like the same
 * coffee-table experience.
 *
 * Structure:
 *   • Full-bleed editorial hero (Ken Burns zoom) on the left (desktop) or
 *     top 45vh (mobile). Uses `branding.cover_image_url` — configurable
 *     via /api/admin/branding, no code change needed to swap the photo.
 *   • Trust ribbon (★★★★★ + testimonial) directly below the hero as a
 *     thin editorial band.
 *   • Floating white card holds a 3-stop editorial progress rail
 *     (01 · 02 · 03 with a warm-gold connector line) and 4 luxury
 *     sections. Service selection uses card chips that turn tan when
 *     picked. CTA lifts + arrow slides + warm glow.
 *   • Thank-you screen mirrors the /proposal page hero language +
 *     WhatsApp + Instagram CTAs (both graceful-hide when unconfigured).
 */

const DEFAULT_COVER =
  "https://customer-assets-4nw71qhi.emergentagent.net/" +
  "job_how-i-do-before/artifacts/roc6cvty_A%26R-76.webp";

const EVENT_PRESETS = [
  "Haldi", "Mehendi", "Sangeet", "Wedding", "Reception",
  "Engagement", "Pre-Wedding Shoot",
];

// Brand tokens — locked to the /proposal page palette for continuity.
const CREAM = "#F8F4EE";
const TAN = "#B6865B";
const TAN_HOVER = "#A0754E";
const INK = "#3B2F24";
const HAIRLINE = "#E7D5B4";

// Icon lookup for service cards. Falls back to Aperture for anything not
// explicitly mapped — the backend keys aren't fixed so this handles any
// custom service the studio adds later.
const SERVICE_ICON = {
  candid_photo: Camera,
  traditional_photo: Camera,
  cinematography: Film,
  traditional_video: Video,
  drone: Sunrise,
  same_day_edit: Video,
  pre_wedding: Heart,
  album: BookOpen,
  family: Users,
};

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

// ------------------------------------------------------------------
// Editorial progress rail — "01 Couple ─── 02 Events ─── 03 Services"
// ------------------------------------------------------------------
function EditorialRail({ activeStep, done }) {
  const stops = [
    { i: 1, label: "Couple", complete: done[0] },
    { i: 2, label: "Events", complete: done[1] },
    { i: 3, label: "Services", complete: done[2] },
  ];
  return (
    <div className="flex items-center gap-2 sm:gap-3 mb-8" data-testid="inquiry-step-rail">
      {stops.map((s, idx) => {
        const isActive = activeStep === s.i;
        const isDone = s.complete;
        const numColor = isDone || isActive ? TAN : "#B8A78E";
        const labelColor = isDone || isActive ? INK : "#A99A82";
        return (
          <div key={s.i} className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div
              className="flex flex-col items-start min-w-0"
              data-testid={`inquiry-step-${s.i}`}
            >
              <div
                className="text-[11px] tracking-[0.35em] font-medium transition-colors duration-300 flex items-center gap-1.5"
                style={{ color: numColor, fontFeatureSettings: '"tnum"' }}
              >
                {String(s.i).padStart(2, "0")}
                {isDone && (
                  <Check className="w-3 h-3" strokeWidth={3} style={{ color: TAN }} />
                )}
              </div>
              <div
                className="text-[13px] mt-0.5 transition-colors duration-300"
                style={{
                  color: labelColor,
                  fontFamily: "'Playfair Display', serif",
                  fontStyle: "italic",
                }}
              >
                {s.label}
              </div>
            </div>
            {idx < stops.length - 1 && (
              <div
                className="hidden sm:block h-px w-10 md:w-14 transition-colors duration-500"
                style={{ background: isDone ? TAN : HAIRLINE }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------
// Floating-label input — label sits inside the field and lifts on focus.
// ------------------------------------------------------------------
function FloatInput({ label, icon: Icon, testid, required, value, onChange, type = "text", ...rest }) {
  const [focused, setFocused] = useState(false);
  const filled = String(value ?? "").length > 0 || type === "date";
  const lift = focused || filled;
  return (
    <label className="block relative">
      <div
        className={`relative rounded-2xl bg-white border transition-all duration-[250ms] ${
          focused ? "-translate-y-px shadow-md" : "shadow-[0_1px_2px_rgba(140,90,50,0.04)]"
        }`}
        style={{
          borderColor: focused ? TAN : HAIRLINE,
          boxShadow: focused ? `0 0 0 3px rgba(182,134,91,0.14)` : undefined,
        }}
      >
        {Icon && (
          <Icon
            className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10"
            style={{ color: focused ? TAN : "#B8A78E" }}
            strokeWidth={1.6}
          />
        )}
        <span
          className="absolute pointer-events-none transition-all duration-[220ms]"
          style={{
            left: Icon ? 42 : 16,
            top: lift ? 6 : "50%",
            transform: lift ? "translateY(0)" : "translateY(-50%)",
            fontSize: lift ? 10 : 13,
            letterSpacing: lift ? "0.22em" : "0.02em",
            textTransform: lift ? "uppercase" : "none",
            color: focused ? TAN : "#8c7a63",
            fontFamily: "'IBM Plex Sans', sans-serif",
          }}
        >
          {label}{required && <span className="ml-0.5" style={{ color: TAN }}>*</span>}
        </span>
        <input
          {...rest}
          type={type}
          value={value}
          onChange={onChange}
          data-testid={testid}
          onFocus={(e) => { setFocused(true); rest.onFocus?.(e); }}
          onBlur={(e) => { setFocused(false); rest.onBlur?.(e); }}
          className={`w-full bg-transparent outline-none text-[15px] rounded-2xl ${Icon ? "pl-11" : "pl-4"} pr-4 pt-6 pb-2.5`}
          style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: INK }}
        />
      </div>
    </label>
  );
}

function FloatSelect({ label, testid, required, value, onChange, options }) {
  const [focused, setFocused] = useState(false);
  const filled = String(value ?? "").length > 0;
  const lift = focused || filled;
  return (
    <label className="block relative">
      <div
        className={`relative rounded-2xl bg-white border transition-all duration-[250ms] ${
          focused ? "-translate-y-px shadow-md" : "shadow-[0_1px_2px_rgba(140,90,50,0.04)]"
        }`}
        style={{
          borderColor: focused ? TAN : HAIRLINE,
          boxShadow: focused ? `0 0 0 3px rgba(182,134,91,0.14)` : undefined,
        }}
      >
        <span
          className="absolute pointer-events-none transition-all duration-[220ms]"
          style={{
            left: 16,
            top: lift ? 6 : "50%",
            transform: lift ? "translateY(0)" : "translateY(-50%)",
            fontSize: lift ? 10 : 13,
            letterSpacing: lift ? "0.22em" : "0.02em",
            textTransform: lift ? "uppercase" : "none",
            color: focused ? TAN : "#8c7a63",
            fontFamily: "'IBM Plex Sans', sans-serif",
          }}
        >
          {label}{required && <span className="ml-0.5" style={{ color: TAN }}>*</span>}
        </span>
        <select
          value={value}
          onChange={onChange}
          data-testid={testid}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="w-full bg-transparent outline-none text-[15px] pl-4 pr-10 pt-6 pb-2.5 rounded-2xl appearance-none cursor-pointer"
          style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: INK }}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <ChevronDown className="w-4 h-4 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "#B8A78E" }} />
      </div>
    </label>
  );
}

// ------------------------------------------------------------------
// Section shell — Playfair heading + subtle tan eyebrow number
// ------------------------------------------------------------------
function Section({ number, title, subtitle, children, testid, delay = 0 }) {
  return (
    <section
      className="opacity-0"
      style={{ animation: `kbFadeUp 600ms ${delay}ms both` }}
      data-testid={testid}
    >
      <div className="flex items-baseline gap-3 mb-1.5">
        <span
          className="text-[10px] tracking-[0.4em] font-medium"
          style={{ color: TAN, fontFeatureSettings: '"tnum"' }}
        >
          {String(number).padStart(2, "0")}
        </span>
        <div className="h-px flex-1" style={{ background: HAIRLINE }} />
      </div>
      <h3
        className="text-xl sm:text-2xl mb-1"
        style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: INK }}
      >
        {title}
      </h3>
      {subtitle && (
        <p className="text-[13px] mb-5" style={{ color: "#7a6853" }}>{subtitle}</p>
      )}
      {children}
    </section>
  );
}

// ------------------------------------------------------------------
// Expandable event-day card
// ------------------------------------------------------------------
function DayCard({ index, day, isPrimary, canRemove, onPatch, onRemove }) {
  const [open, setOpen] = useState(!!day.date || isPrimary);
  useEffect(() => { if (day.date) setOpen(true); }, [day.date]);

  const heading = day.event_type || (isPrimary ? "Primary event" : `Day ${index + 1}`);
  const meta = day.date
    ? new Date(day.date + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
    : "Tap to add date";

  return (
    <div
      className="rounded-2xl bg-white border transition-all duration-[250ms]"
      style={{
        borderColor: open ? TAN : HAIRLINE,
        boxShadow: open ? "0 8px 24px rgba(140,90,50,0.07)" : "0 1px 3px rgba(140,90,50,0.03)",
      }}
      data-testid={`inquiry-day-row-${index}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
      >
        <div className="min-w-0 flex-1 flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 transition-all duration-300"
            style={{
              background: day.date ? TAN : "#F5EFE4",
              color: day.date ? "#fff" : "#B8A78E",
              boxShadow: day.date ? "0 4px 10px rgba(182,134,91,0.25)" : undefined,
            }}
          >
            {String(index + 1).padStart(2, "0")}
          </div>
          <div className="min-w-0">
            <div className="text-[15px] font-medium truncate" style={{ fontFamily: "'Playfair Display', serif", color: INK }}>
              {heading}
            </div>
            <div className="text-[12px] truncate" style={{ color: "#8c7a63" }}>
              {meta}{day.venue ? ` · ${day.venue}` : ""}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canRemove && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onRemove(); } }}
              className="p-1.5 rounded-full text-[#B8A78E] hover:text-red-700 hover:bg-red-50 transition-colors"
              title="Remove day"
              data-testid={`inquiry-day-remove-${index}`}
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
            </span>
          )}
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-[250ms] ${open ? "rotate-180" : ""}`}
            style={{ color: TAN }}
          />
        </div>
      </button>
      {open && (
        <div
          className="px-5 pb-5 grid grid-cols-1 sm:grid-cols-3 gap-3"
          style={{ animation: "kbFadeUp 350ms both" }}
        >
          <FloatInput
            label="Date"
            icon={Calendar}
            type="date"
            min={todayIso()}
            value={day.date}
            required={isPrimary}
            onChange={(e) => onPatch({ date: e.target.value })}
            testid={`inquiry-day-date-${index}`}
          />
          <FloatSelect
            label="Event"
            value={day.event_type || ""}
            onChange={(e) => onPatch({ event_type: e.target.value })}
            testid={`inquiry-day-event-${index}`}
            options={[
              { value: "", label: "— Select —" },
              ...EVENT_PRESETS.map((ep) => ({ value: ep, label: ep })),
            ]}
          />
          <FloatInput
            label="Venue"
            icon={MapPin}
            placeholder=""
            value={day.venue}
            onChange={(e) => onPatch({ venue: e.target.value })}
            testid={`inquiry-day-venue-${index}`}
          />
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Service card — luxury selectable tile w/ animated tick
// ------------------------------------------------------------------
function ServiceCard({ service, checked, onToggle }) {
  const Icon = SERVICE_ICON[service.key] || Aperture;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="group relative rounded-2xl px-4 py-4 text-left border transition-all duration-[250ms] hover:-translate-y-0.5"
      style={
        checked
          ? {
              background: TAN,
              borderColor: TAN,
              color: "#fff",
              boxShadow: "0 12px 28px rgba(182,134,91,0.28)",
            }
          : {
              background: "#fff",
              borderColor: HAIRLINE,
              color: INK,
              boxShadow: "0 1px 2px rgba(140,90,50,0.04)",
            }
      }
      onMouseOver={(e) => { if (!checked) e.currentTarget.style.borderColor = TAN; }}
      onMouseOut={(e) => { if (!checked) e.currentTarget.style.borderColor = HAIRLINE; }}
      data-testid={`inquiry-service-${service.key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <Icon
          className="w-5 h-5 shrink-0 transition-transform duration-[250ms] group-hover:scale-110"
          style={{ color: checked ? "rgba(255,255,255,0.9)" : TAN }}
          strokeWidth={1.4}
        />
        {/* Animated check — scales in when selected */}
        <span
          className="w-5 h-5 rounded-full flex items-center justify-center transition-all duration-[300ms]"
          style={{
            background: checked ? "#fff" : "transparent",
            transform: checked ? "scale(1)" : "scale(0.6)",
            opacity: checked ? 1 : 0,
          }}
        >
          <Check className="w-3 h-3" strokeWidth={3} style={{ color: TAN }} />
        </span>
      </div>
      <div
        className="mt-3 text-[14px] leading-tight"
        style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
      >
        {service.label}
      </div>
    </button>
  );
}

// ------------------------------------------------------------------
// Ken Burns + fade-up keyframes now live in `@/lib/brand` (KEYFRAMES).
// We keep this constant as an alias so the page code below reads the
// same, but the actual CSS comes from the shared design system.
// ------------------------------------------------------------------
const HERO_KEYFRAMES = KEYFRAMES;

// ==================================================================
// MAIN PAGE
// ==================================================================
export default function Inquiry() {
  const branding = useBranding();
  const coverUrl = branding.cover_image_url || DEFAULT_COVER;
  const [services, setServices] = useState([]);
  const [whatsapp, setWhatsapp] = useState("");
  const [instagram, setInstagram] = useState("");
  const [form, setForm] = useState({
    couple_names: "",
    phone: "",
    email: "",
    event_days: [{ date: "", event_type: "Wedding", venue: "" }],
    services: [],
    notes: "",
    _hp: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/services`).then((r) => setServices(r.data.services || [])).catch(() => setServices([]));
    axios.get(`${API}/public/branding`).then((r) => {
      setWhatsapp(r.data.whatsapp_number || "");
      setInstagram(r.data.instagram_url || "");
    }).catch(() => {});
  }, []);

  const addDay = () => setForm((f) => ({
    ...f,
    event_days: [...f.event_days, { date: "", event_type: "", venue: "" }],
  }));
  const removeDay = (i) => setForm((f) => ({
    ...f,
    event_days: f.event_days.filter((_, idx) => idx !== i),
  }));
  const patchDay = (i, patch) => setForm((f) => ({
    ...f,
    event_days: f.event_days.map((d, idx) => (idx === i ? { ...d, ...patch } : d)),
  }));
  const toggleService = (key) => setForm((f) => ({
    ...f,
    services: f.services.includes(key)
      ? f.services.filter((s) => s !== key)
      : [...f.services, key],
  }));

  // Progress derivation — purely visual, no state machine.
  const doneSteps = [
    form.couple_names.trim().length >= 2 && form.phone.trim().length >= 7,
    form.event_days.some((d) => (d.date || "").trim()),
    form.services.length > 0,
  ];
  const activeStep = !doneSteps[0] ? 1 : !doneSteps[1] ? 2 : 3;

  const canSubmit = useMemo(() => (
    form.couple_names.trim().length >= 2 &&
    form.phone.trim().length >= 7 &&
    form.event_days.some((d) => (d.date || "").trim()) &&
    !busy
  ), [form, busy]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {
        ...form,
        event_days: form.event_days.filter((d) => (d.date || "").trim()),
      };
      const { data } = await axios.post(`${API}/public/inquiries`, payload);
      setDone({ reference: data.reference });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  // ================================================================
  // THANK-YOU VIEW
  // ================================================================
  if (done) {
    const cleanNumber = whatsapp.replace(/[^\d]/g, "");
    const waHref = cleanNumber
      ? `https://wa.me/${cleanNumber}?text=${encodeURIComponent(
          `Hi Kahani Studio — I just submitted an inquiry (ref ${done.reference}). Looking forward to hearing from you.`
        )}`
      : "";
    const igHref = instagram
      ? (instagram.startsWith("http") ? instagram : `https://instagram.com/${instagram.replace(/^@/, "")}`)
      : "";

    return (
      <div
        className="min-h-screen"
        style={{ background: CREAM, fontFamily: "'IBM Plex Sans', sans-serif", color: INK }}
        data-testid="inquiry-thanks-page"
      >
        <style>{HERO_KEYFRAMES}</style>

        {/* Hero — same photo with grain + vignette from shared brand.js. */}
        <div className="relative w-full overflow-hidden" style={{ height: "42vh", minHeight: 280 }}>
          <div
            className="absolute inset-0 bg-cover bg-center kb-ken"
            style={{ backgroundImage: `url('${coverUrl}')` }}
          />
          <div className="absolute inset-0 kb-vignette" />
          <div className="kb-grain" />
          <div className="relative h-full flex flex-col justify-end px-6 sm:px-10 pb-10">
            <div className="text-[10px] tracking-[0.4em] uppercase mb-3 text-white/85 kb-fade">
              Kahani by Rakesh Janardan
            </div>
            <div
              className="text-white text-4xl sm:text-5xl leading-[1.05] kb-fade"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, animationDelay: "120ms" }}
            >
              Thank you.
            </div>
          </div>
        </div>

        <div className="max-w-lg w-full mx-auto px-5 sm:px-8 -mt-14 relative pb-16">
          <div
            className="bg-white rounded-3xl p-8 sm:p-12 text-center opacity-0"
            style={{
              boxShadow: "0 30px 80px rgba(140,90,50,0.12)",
              border: `1px solid ${HAIRLINE}`,
              animation: "kbFadeUp 600ms 200ms both",
            }}
          >
            <div className="text-4xl mb-3 select-none" aria-hidden="true">✨</div>
            <div className="text-[10px] tracking-[0.35em] uppercase mb-3" style={{ color: TAN }}>
              Inquiry received
            </div>
            <h1
              className="text-3xl sm:text-4xl leading-tight mb-4"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: INK }}
            >
              We've received your enquiry.
            </h1>
            <p className="text-[15px] leading-relaxed" style={{ color: "#5a4a38" }}>
              Your personalised proposal will be crafted within{" "}
              <span style={{ color: TAN, fontWeight: 600 }}>24 hours</span>.
            </p>

            <div className="mt-8 flex flex-col items-center gap-2">
              <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: "#8c7a63" }}>
                Reference
              </div>
              <div
                className="font-mono text-base px-4 py-1.5 rounded-full"
                style={{ background: "#F5EFE4", color: INK, border: `1px solid ${HAIRLINE}` }}
                data-testid="inquiry-reference"
              >
                {done.reference}
              </div>
            </div>

            {(waHref || igHref) && (
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                {waHref && (
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white transition-all duration-[250ms] hover:-translate-y-px kb-glow-hover"
                    style={{ background: TAN, boxShadow: "0 8px 20px rgba(182,134,91,0.28)" }}
                    onMouseOver={(e) => (e.currentTarget.style.background = TAN_HOVER)}
                    onMouseOut={(e) => (e.currentTarget.style.background = TAN)}
                    data-testid="inquiry-thanks-whatsapp"
                  >
                    <MessageCircle className="w-4 h-4" strokeWidth={1.8} />
                    <span className="text-[13px] tracking-wider uppercase">WhatsApp</span>
                  </a>
                )}
                {igHref && (
                  <a
                    href={igHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-[250ms] hover:-translate-y-px"
                    style={{ background: "#fff", color: INK, border: `1px solid ${HAIRLINE}` }}
                    onMouseOver={(e) => { e.currentTarget.style.borderColor = TAN; }}
                    onMouseOut={(e) => { e.currentTarget.style.borderColor = HAIRLINE; }}
                    data-testid="inquiry-thanks-instagram"
                  >
                    <Instagram className="w-4 h-4" strokeWidth={1.6} style={{ color: TAN }} />
                    <span className="text-[13px] tracking-wider uppercase">Instagram</span>
                  </a>
                )}
              </div>
            )}

            <a
              href="/inquiry"
              className="block mt-8 text-[11px] tracking-[0.25em] uppercase transition-colors"
              style={{ color: "#8c7a63" }}
              onMouseOver={(e) => (e.currentTarget.style.color = TAN)}
              onMouseOut={(e) => (e.currentTarget.style.color = "#8c7a63")}
              data-testid="inquiry-new"
            >
              Submit another inquiry
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ================================================================
  // MAIN INQUIRY FORM
  // ================================================================
  return (
    <div
      className="min-h-screen w-full"
      style={{ background: CREAM, fontFamily: "'IBM Plex Sans', sans-serif", color: INK }}
      data-testid="inquiry-page"
    >
      <style>{HERO_KEYFRAMES}</style>

      <div className="flex flex-col md:flex-row">
        {/* ============================================================
             HERO — Ken Burns editorial cover
             Desktop: 50% width, sticks to viewport top as the form scrolls.
             Mobile: 45vh, form card slides up with -mt-8 handoff.
        ============================================================ */}
        <aside
          className="relative w-full md:w-[50%] md:sticky md:top-0 md:h-screen shrink-0 overflow-hidden"
          style={{ height: "45vh" }}
          data-testid="inquiry-cover"
        >
          {/* Ken Burns image layer — slow 30s zoom via shared brand.js */}
          <div
            className="absolute inset-0 bg-cover kb-ken"
            style={{
              backgroundImage: `url('${coverUrl}')`,
              // Focal-point positioning — bottom-anchored keeps the couple
              // visible across all responsive crops (they're in the lower
              // third of the reference photo).
              backgroundPosition: "center 65%",
            }}
          />
          {/* Editorial vignette (radial + linear from shared brand.js) */}
          <div className="absolute inset-0 kb-vignette" />
          {/* Subtle film grain — cinematic warmth, keeps the photo from
              feeling too digital. Runs on GPU. */}
          <div className="kb-grain" />

          {/* Hero content — fade up, staggered */}
          <div className="relative h-full flex flex-col justify-end px-6 sm:px-10 md:px-16 pb-10 md:pb-20 pt-12">
            <div
              className="text-[10px] tracking-[0.45em] uppercase mb-6 text-white/85 opacity-0"
              style={{ animation: "kbFadeUp 700ms 100ms both" }}
            >
              Kahani by Rakesh Janardan
            </div>
            <h1
              className="text-white text-4xl sm:text-5xl md:text-[3.5rem] lg:text-[4rem] leading-[1.02] mb-5 max-w-2xl opacity-0"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 500,
                letterSpacing: "-0.01em",
                animation: "kbFadeUp 900ms 250ms both",
              }}
            >
              Let's create something timeless.
            </h1>
            <p
              className="text-white/85 text-[14px] sm:text-[16px] leading-relaxed max-w-md mb-6 opacity-0"
              style={{ animation: "kbFadeUp 900ms 450ms both" }}
            >
              Every wedding has a story.<br className="hidden sm:block" />
              {" "}Let us preserve yours with honesty, emotion and timeless imagery.
            </p>
            <div
              className="pt-6 border-t border-white/25 text-white/75 text-[11px] tracking-[0.28em] uppercase space-y-1.5 opacity-0"
              style={{ animation: "kbFadeUp 900ms 650ms both" }}
            >
              <div>800+ Weddings Captured</div>
              <div>Trusted Across Maharashtra</div>
              <div>Luxury Wedding Photography &amp; Films</div>
            </div>
            <div
              className="md:hidden mt-6 flex items-center gap-2 text-white/85 text-[11px] tracking-[0.3em] uppercase opacity-0"
              style={{ animation: "kbFadeUp 900ms 850ms both" }}
            >
              <ArrowDown className="w-3.5 h-3.5 animate-bounce" strokeWidth={1.4} />
              Begin below
            </div>
          </div>
        </aside>

        {/* ============================================================
             FORM COLUMN
        ============================================================ */}
        <main className="flex-1 min-w-0 relative">
          <div className="max-w-xl w-full mx-auto px-5 sm:px-8 md:px-10 pt-6 md:pt-16 pb-16 relative -mt-10 md:mt-0">
            {/* Trust ribbon — thin editorial band, only above the card */}
            <div
              className="mb-4 rounded-full px-5 py-2.5 flex items-center gap-3 opacity-0"
              style={{
                background: "#fff",
                border: `1px solid ${HAIRLINE}`,
                boxShadow: "0 4px 12px rgba(140,90,50,0.05)",
                animation: "kbFadeUp 700ms 400ms both",
              }}
              data-testid="inquiry-trust-ribbon"
            >
              <div className="flex gap-0.5 shrink-0">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5" style={{ color: TAN, fill: TAN }} strokeWidth={0} />
                ))}
              </div>
              <p
                className="text-[12px] italic truncate flex-1"
                style={{ fontFamily: "'Playfair Display', serif", color: "#5a4a38" }}
              >
                "A dream to work with. Every moment felt beautifully documented."
              </p>
            </div>

            {/* Floating form card */}
            <div
              className="bg-white rounded-3xl p-6 sm:p-10 opacity-0"
              style={{
                boxShadow: "0 30px 80px rgba(140,90,50,0.10)",
                border: "1px solid #EFE4CE",
                animation: "kbFadeUp 700ms 250ms both",
              }}
            >
              <EditorialRail activeStep={activeStep} done={doneSteps} />

              <form onSubmit={submit} className="space-y-10" data-testid="inquiry-form">
                {/* 1 — COUPLE */}
                <Section
                  number={1}
                  title="The couple"
                  subtitle="Who should we address the proposal to?"
                  testid="inquiry-section-couple"
                  delay={300}
                >
                  <div className="space-y-3">
                    <FloatInput
                      label="Couple names"
                      required
                      icon={Sparkles}
                      value={form.couple_names}
                      onChange={(e) => setForm((f) => ({ ...f, couple_names: e.target.value }))}
                      placeholder=""
                      autoFocus
                      testid="inquiry-couple"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <FloatInput
                        label="Phone / WhatsApp"
                        required
                        icon={Phone}
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                        testid="inquiry-phone"
                      />
                      <FloatInput
                        label="Email"
                        type="email"
                        icon={Mail}
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        testid="inquiry-email"
                      />
                    </div>
                  </div>
                </Section>

                {/* 2 — EVENTS */}
                <Section
                  number={2}
                  title="Event details"
                  subtitle="Add every function you'd like us to cover."
                  testid="inquiry-section-events"
                  delay={400}
                >
                  <div className="space-y-3">
                    {form.event_days.map((d, i) => (
                      <DayCard
                        key={i}
                        index={i}
                        day={d}
                        isPrimary={i === 0}
                        canRemove={form.event_days.length > 1}
                        onPatch={(p) => patchDay(i, p)}
                        onRemove={() => removeDay(i)}
                      />
                    ))}
                    <button
                      type="button"
                      onClick={addDay}
                      className="w-full py-3 rounded-2xl border border-dashed text-[13px] tracking-wider uppercase transition-all duration-[250ms] hover:-translate-y-px inline-flex items-center justify-center gap-2"
                      style={{ borderColor: TAN, color: TAN }}
                      onMouseOver={(e) => { e.currentTarget.style.background = "#FBF6EE"; }}
                      onMouseOut={(e) => { e.currentTarget.style.background = "transparent"; }}
                      data-testid="inquiry-add-day"
                    >
                      <Plus className="w-3.5 h-3.5" strokeWidth={1.6} />
                      Add another day
                    </button>
                  </div>
                </Section>

                {/* 3 — SERVICES */}
                <Section
                  number={3}
                  title="Services"
                  subtitle="Pick everything you'd like us to craft."
                  testid="inquiry-section-services"
                  delay={500}
                >
                  {services.length === 0 ? (
                    <div className="text-[12px] italic" style={{ color: "#8c7a63" }}>Loading services…</div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {services.map((s) => (
                        <ServiceCard
                          key={s.key}
                          service={s}
                          checked={form.services.includes(s.key)}
                          onToggle={() => toggleService(s.key)}
                        />
                      ))}
                    </div>
                  )}
                </Section>

                {/* 4 — NOTES */}
                <Section
                  number={4}
                  title="A little more"
                  subtitle="Approximate budget, style preferences, mood — anything that helps us tailor the proposal."
                  testid="inquiry-section-notes"
                  delay={600}
                >
                  <div
                    className="rounded-2xl bg-white border transition-all duration-[250ms] focus-within:shadow-md"
                    style={{ borderColor: HAIRLINE }}
                  >
                    <textarea
                      rows={4}
                      className="w-full bg-transparent outline-none text-[15px] py-4 px-4 rounded-2xl resize-none"
                      style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: INK }}
                      value={form.notes}
                      onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                      placeholder="Tell us anything else…"
                      data-testid="inquiry-notes"
                    />
                  </div>
                </Section>

                {/* Honeypot */}
                <input
                  type="text"
                  name="_hp"
                  value={form._hp}
                  onChange={(e) => setForm((f) => ({ ...f, _hp: e.target.value }))}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute w-px h-px opacity-0 pointer-events-none -left-96"
                  data-testid="inquiry-honeypot"
                />

                {err && (
                  <div
                    className="text-sm rounded-xl px-4 py-3"
                    style={{ background: "#FEF2F2", border: "1px solid #FEE2E2", color: "#B91C1C", animation: "kbFadeUp 350ms both" }}
                    data-testid="inquiry-error"
                  >
                    {err}
                  </div>
                )}

                {/* CTA */}
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="group w-full inline-flex items-center justify-center gap-3 py-4 rounded-full transition-all duration-[250ms] disabled:opacity-50 disabled:cursor-not-allowed hover:enabled:-translate-y-0.5 kb-glow-hover"
                  style={{
                    background: TAN,
                    color: "#fff",
                    boxShadow: "0 14px 30px rgba(182,134,91,0.28)",
                  }}
                  onMouseOver={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = TAN_HOVER; }}
                  onMouseOut={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = TAN; }}
                  data-testid="inquiry-submit"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  <span
                    className="text-[13px] tracking-[0.28em] uppercase"
                    style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                  >
                    Create My Proposal
                  </span>
                  <ArrowRight
                    className="w-4 h-4 transition-transform duration-[250ms] group-hover:translate-x-1"
                    strokeWidth={1.6}
                  />
                </button>
                <p className="text-[11px] text-center tracking-wider" style={{ color: "#8c7a63" }}>
                  We'll respond over WhatsApp / email within 24 hours.
                </p>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
